import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Pagination,
  Chip,
  Button,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { CalendarToday, Person, AccessTime } from '@mui/icons-material';
import contentService, { BlogPost, BlogTag } from '../services/contentService';
import { translateWithCache } from '../services/translation';

const BlogPage: React.FC = () => {
  const { i18n } = useTranslation();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        const [postsData, tagsData] = await Promise.all([
          contentService.getBlogPosts({ page: 1, limit: 9 }),
          contentService.getBlogTags(),
        ]);

        const lang = (i18n.language || 'es').split('-')[0];

        let translatedPosts = postsData.posts;
        let translatedTags = tagsData;

        if (lang === 'en') {
          // Translate blog posts
          translatedPosts = await Promise.all(
            postsData.posts.map(async (post) => ({
              ...post,
              title: await translateWithCache(post.title, 'es', 'en'),
              content: await translateWithCache(post.content, 'es', 'en'),
              excerpt: post.excerpt ? await translateWithCache(post.excerpt, 'es', 'en') : post.excerpt,
              tags: await Promise.all(post.tags.map(async (tag) => await translateWithCache(tag, 'es', 'en'))),
            }))
          );

          // Translate blog tags
          translatedTags = await Promise.all(
            tagsData.map(async (tag) => ({
              ...tag,
              name: await translateWithCache(tag.name, 'es', 'en'),
            }))
          );
        }

        setBlogPosts(translatedPosts);
        setTotalPages(postsData.totalPages);
        setTags(translatedTags);
      } catch (err: any) {
        console.error('Error fetching blog data:', err);
        setError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, [i18n.language]);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const postsData = await contentService.getBlogPosts({
        search: searchTerm || undefined,
        tag: selectedTag || undefined,
        page: 1,
        limit: 9,
      });

      const lang = (i18n.language || 'es').split('-')[0];

      let translatedPosts = postsData.posts;

      if (lang === 'en') {
        // Translate blog posts
        translatedPosts = await Promise.all(
          postsData.posts.map(async (post) => ({
            ...post,
            title: await translateWithCache(post.title, 'es', 'en'),
            content: await translateWithCache(post.content, 'es', 'en'),
            excerpt: post.excerpt ? await translateWithCache(post.excerpt, 'es', 'en') : post.excerpt,
            tags: await Promise.all(post.tags.map(async (tag) => await translateWithCache(tag, 'es', 'en'))),
          }))
        );
      }

      setBlogPosts(translatedPosts);
      setTotalPages(postsData.totalPages);
      setPage(1);
    } catch (err: any) {
      console.error('Error searching blog posts:', err);
      setError('Failed to search blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setLoading(true);
    try {
      const postsData = await contentService.getBlogPosts({
        search: searchTerm || undefined,
        tag: selectedTag || undefined,
        page: value,
        limit: 9,
      });

      const lang = (i18n.language || 'es').split('-')[0];

      let translatedPosts = postsData.posts;

      if (lang === 'en') {
        // Translate blog posts
        translatedPosts = await Promise.all(
          postsData.posts.map(async (post) => ({
            ...post,
            title: await translateWithCache(post.title, 'es', 'en'),
            content: await translateWithCache(post.content, 'es', 'en'),
            excerpt: post.excerpt ? await translateWithCache(post.excerpt, 'es', 'en') : post.excerpt,
            tags: await Promise.all(post.tags.map(async (tag) => await translateWithCache(tag, 'es', 'en'))),
          }))
        );
      }

      setBlogPosts(translatedPosts);
      setTotalPages(postsData.totalPages);
    } catch (err: any) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleTagChange = (event: SelectChangeEvent) => {
    setSelectedTag(event.target.value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: '400px',
          backgroundImage: 'url(/images/coffee-beans.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography
              variant="h1"
              gutterBottom
              sx={{
                fontFamily: '"Bebas Neue", sans-serif',
                fontWeight: 400,
                mb: 2,
              }}
            >
              Blog
            </Typography>
            <Typography
              variant="h5"
              sx={{
                opacity: 0.9,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.4,
              }}
            >
              Descubre historias, consejos y noticias sobre el mundo del café y la gastronomía.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Blog Content */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          {/* Search and Filter */}
          <Box sx={{ mb: 6 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Buscar artículos"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Filtrar por etiqueta</InputLabel>
                  <Select
                    value={selectedTag}
                    label="Filtrar por etiqueta"
                    onChange={handleTagChange}
                  >
                    <MenuItem value="">
                      <em>Todas las etiquetas</em>
                    </MenuItem>
                    {tags.map((tag) => (
                      <MenuItem key={tag.id} value={tag.name}>
                        {tag.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  fullWidth
                  sx={{
                    backgroundColor: 'primary.main',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      opacity: 0.9,
                    },
                  }}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Blog Posts Grid */}
          {blogPosts.length === 0 ? (
            <Box textAlign="center" sx={{ py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No se encontraron artículos.
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={4}>
                {blogPosts.map((post) => (
                  <Grid item xs={12} md={6} lg={4} key={post.id}>
                    <Card
                      component={RouterLink}
                      to={`/blog/${post.slug}`}
                      sx={{
                        height: '100%',
                        textDecoration: 'none',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      {post.featured_image && (
                        <CardMedia
                          component="img"
                          height="200"
                          image={post.featured_image}
                          alt={post.title}
                          sx={{ objectFit: 'cover' }}
                        />
                      )}
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          {post.tags?.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          gutterBottom
                          sx={{
                            fontFamily: '"Bebas Neue", sans-serif',
                            fontWeight: 400,
                            mb: 2,
                            lineHeight: 1.2,
                          }}
                        >
                          {post.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 3, lineHeight: 1.6 }}
                        >
                          {post.excerpt}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.secondary' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarToday fontSize="small" />
                            <Typography variant="caption">
                              {post.published_at ? formatDate(post.published_at) : formatDate(post.created_at)}
                            </Typography>
                          </Box>
                          {post.author && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Person fontSize="small" />
                              <Typography variant="caption">
                                {post.author}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default BlogPage; 